/**
 * SequencesManager — "רצפי שליחה" tab of Email Automation.
 *
 * A sequence = trigger tags (who enters) + emails (what they get) + on/off. Server rules:
 * services/service.emailSequences.js. Statuses: טיוטה (never on) · פעיל · מושהה (was on, now off).
 *
 * List: name (+description), status (tooltip says what it means now), who enters (tag chips), emails,
 * in progress now (enrollmentCounts.active), finished (completed), sent / open rate, on/off switch, edit / delete.
 * Every action first shows what it will do (useImpactConfirm → POST /api/email/sequences/impact, a dry run):
 *   switch ON  → who enters, old paused / frozen customers continuing, overdue emails, and the
 *                "existing customers" question → POST /:id/activate { includeExisting }
 *   switch OFF → how many freeze in place, how many had an email due in 24h → POST /:id/deactivate
 *   delete     → who stops, sent history / stats → DELETE /:id ("השהה במקום" offered for an active sequence)
 * New / edit opens SequenceUpdater (drawer). Reloads when its tab becomes active (prop activeTab).
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import * as actionsCreators from './../../../actions/index.js';
import { DataTable, RowActions, EmptyState, Button, Switch, StatusBadge, TagChips, Tooltip, useBreakpoint } from '../../../ui';
import SequenceUpdater from './SequenceUpdater';
import useImpactConfirm from './useImpactConfirm';
import { SEQUENCE_STATUS, statusOf, statusExplanation, sequenceErrorText } from './sequenceHelpers';
import classes from './TemplatesManager.css';
import localClasses from './SequencesManager.css';

const openRateColor = (rate) => (rate > 50 ? '#16A34A' : rate > 25 ? '#D97706' : '#71717A');

const SequencesManager = (props) => {
  const [sequences, setSequences] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, object = edit
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const screens = useBreakpoint();

  const [confirmImpact, impactDialog] = useImpactConfirm({
    getImpact: (body) => props.getSequenceImpact(body),
    showAlert: props.showAlert,
  });

  useEffect(() => {
    loadSequences();
    if (props.activeTab === 'sequences') {
      setTimeout(() => {
        loadSequences();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeTab]);

  const loadSequences = async () => {
    setLoading(true);
    try {
      const results = await props.getEmailSequences();
      setSequences(results);
    } catch (error) {
      console.error('Error loading sequences:', error);
      props.showAlert('שגיאה בטעינת רצפים');
    }
    setLoading(false);
  };

  const onDeleteSequence = async (sequence) => {
    const answer = await confirmImpact(
      { action: 'delete', sequenceId: sequence.id },
      {
        title: `למחוק את הרצף "${sequence.name}"?`,
        lead: sequence.isActive ? 'רוצים רק לעצור את הרצף? אפשר להשהות אותו במקום למחוק, ולהפעיל שוב מתי שתרצו.' : null,
        okText: 'מחק',
        danger: true,
        alternate: sequence.isActive ? { text: 'השהה במקום', value: 'pause' } : null,
      }
    );
    if (!answer) return;
    if (answer.alternate === 'pause') {
      await deactivate(sequence);
      return;
    }
    try {
      await props.deleteEmailSequence(sequence.id);
      props.showAlert('הרצף נמחק בהצלחה');
      loadSequences();
    } catch (error) {
      console.error('Error deleting sequence:', error);
      props.showAlert(sequenceErrorText(error, 'שגיאה במחיקת הרצף'));
    }
  };

  const turnOn = async (sequence) => {
    setTogglingId(sequence.id);
    const answer = await confirmImpact({ action: 'activate', sequenceId: sequence.id }, { title: `הפעלת הרצף "${sequence.name}"`, okText: 'הפעל', onLoaded: () => setTogglingId(null) });
    if (!answer) return;
    setTogglingId(sequence.id);
    try {
      const res = await props.activateEmailSequence(sequence.id, answer.includeExisting);
      const n = (res && res.enrolledExisting) || 0;
      props.showAlert('הרצף הופעל' + (n > 0 ? ` · ${n} לקוחות נוספו לרצף` : ''));
      loadSequences();
    } catch (error) {
      console.error('Error activating sequence:', error);
      props.showAlert(sequenceErrorText(error, 'שגיאה בהפעלת הרצף'));
    }
    setTogglingId(null);
  };

  const deactivate = async (sequence) => {
    setTogglingId(sequence.id);
    try {
      await props.deactivateEmailSequence(sequence.id);
      props.showAlert('הרצף הושהה');
      loadSequences();
    } catch (error) {
      console.error('Error deactivating sequence:', error);
      props.showAlert(sequenceErrorText(error, 'שגיאה בהשהיית הרצף'));
    }
    setTogglingId(null);
  };

  const turnOff = async (sequence) => {
    setTogglingId(sequence.id);
    const answer = await confirmImpact({ action: 'deactivate', sequenceId: sequence.id }, { title: `השהיית הרצף "${sequence.name}"`, okText: 'השהה', onLoaded: () => setTogglingId(null) });
    if (answer) await deactivate(sequence);
  };

  const closeEditor = (refresh) => {
    setEditing(undefined);
    if (refresh) loadSequences();
  };

  return (
    <div className={classes.tab}>
      {editing !== undefined ? <SequenceUpdater sequence={editing} onClose={closeEditor} /> : null}
      {impactDialog}

      <div className={localClasses.toolbar}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(null)}>
          רצף חדש
        </Button>
      </div>

      <DataTable
        data={sequences}
        loading={loading}
        fit
        onRowClick={(s) => setEditing(s)}
        empty={
          <EmptyState
            title="אין עדיין רצפים"
            description="רצף = טאגים (מי נכנס) + מיילים (מה מקבלים)"
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(null)}>
                רצף חדש
              </Button>
            }
          />
        }
        columns={[
          {
            key: 'name',
            title: 'שם הרצף',
            render: (_, s) => (
              <div className={localClasses.nameCell}>
                <span className={classes.name}>{s.name}</span>
                {s.description ? <span className={localClasses.description}>{s.description}</span> : null}
              </div>
            ),
          },
          {
            key: 'status',
            title: 'סטטוס',
            width: 90,
            responsive: ['sm'],
            render: (_, s) => {
              const st = SEQUENCE_STATUS[statusOf(s)];
              return (
                <Tooltip title={statusExplanation(s)} mouseEnterDelay={0.3}>
                  <span>
                    <StatusBadge color={st.color}>{st.label}</StatusBadge>
                  </span>
                </Tooltip>
              );
            },
          },
          {
            key: 'tags',
            title: 'מי נכנס',
            responsive: ['md'],
            render: (_, s) => (
              <TagChips tags={(s.triggerTags || []).map((t) => ({ name: (t.tag && t.tag.name) || `#${t.tag_id}` }))} max={3} emptyText="ללא טאגים — אף אחד לא נכנס" />
            ),
          },
          {
            key: 'steps',
            title: 'מיילים',
            width: 70,
            responsive: ['lg'],
            render: (_, s) => <span className="ui-tabular">{(s.steps || []).length}</span>,
          },
          {
            key: 'active',
            title: 'ברצף עכשיו',
            width: 95,
            responsive: ['md'],
            render: (_, s) => <span className="ui-tabular">{(s.enrollmentCounts && s.enrollmentCounts.active) || 0}</span>,
          },
          {
            key: 'completed',
            title: 'סיימו',
            width: 70,
            responsive: ['lg'],
            render: (_, s) => <span className="ui-tabular">{(s.enrollmentCounts && s.enrollmentCounts.completed) || 0}</span>,
          },
          {
            key: 'sent',
            title: 'נשלחו / נפתחו',
            width: 120,
            responsive: ['xl'],
            render: (_, s) => {
              const t = s.tracking || {};
              const rate = t.openRate || 0;
              return (
                <span className={localClasses.sentCell}>
                  <span className="ui-tabular">{t.totalSent || 0}</span>
                  <StatusBadge color={openRateColor(rate)}>{rate}%</StatusBadge>
                </span>
              );
            },
          },
          {
            key: 'switch',
            title: 'פעיל',
            width: 70,
            render: (_, s) => (
              <Tooltip title={s.isActive ? 'השהה רצף' : 'הפעל רצף'} mouseEnterDelay={0.4}>
                <Switch
                  size="small"
                  checked={!!s.isActive}
                  loading={togglingId === s.id}
                  onClick={(checked, e) => {
                    e.stopPropagation();
                    if (s.isActive) turnOff(s);
                    else turnOn(s);
                  }}
                />
              </Tooltip>
            ),
          },
          {
            key: 'actions',
            title: '',
            align: 'left',
            width: screens.md === false ? 64 : 90, // phones: a single "⋯" menu
            render: (_, s) => (
              <RowActions
                actions={[
                  { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => setEditing(s) },
                  { key: 'delete', icon: <DeleteOutlined />, label: 'מחיקה', danger: true, onClick: () => onDeleteSequence(s) },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getEmailSequences: actionsCreators.getEmailSequences,
      deleteEmailSequence: actionsCreators.deleteEmailSequence,
      activateEmailSequence: actionsCreators.activateEmailSequence,
      deactivateEmailSequence: actionsCreators.deactivateEmailSequence,
      getSequenceImpact: actionsCreators.getSequenceImpact,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SequencesManager);
